/**
 * NovaHamsterWheel — Nova running inside a spinning wheel.
 *
 * Pure inline SVG so it can be dropped anywhere with no asset
 * pipeline. The wheel <g> rotates, Nova bobs vertically, and her
 * two legs scissor out of phase. All keyframes live in an inline
 * <style> block — the component is fully self-contained.
 *
 * Honors `prefers-reduced-motion`.
 */
interface NovaHamsterWheelProps {
  size?: number;
  paused?: boolean;
}

export function NovaHamsterWheel({ size = 36, paused = false }: NovaHamsterWheelProps) {
  const playState = paused ? 'paused' : 'running';

  // 8 evenly-spaced spokes inside a 100x100 viewBox centered at (50,50).
  const spokes = Array.from({ length: 8 }, (_, i) => {
    const angle = (i * 360) / 8;
    return (
      <line
        key={i}
        x1="50"
        y1="50"
        x2="50"
        y2="10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.55"
        transform={`rotate(${angle} 50 50)`}
      />
    );
  });

  return (
    <span
      className="inline-block align-middle text-orbit-orange"
      style={{ width: size, height: size, lineHeight: 0 }}
      aria-hidden="true"
    >
      <style>{`
        @keyframes nova-wheel-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes nova-wheel-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-1.2px); }
        }
        @keyframes nova-wheel-leg-l {
          0%, 100% { transform: rotate(-22deg); }
          50%      { transform: rotate(22deg); }
        }
        @keyframes nova-wheel-leg-r {
          0%, 100% { transform: rotate(22deg); }
          50%      { transform: rotate(-22deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nova-wheel-spin, .nova-wheel-bob,
          .nova-wheel-leg-l, .nova-wheel-leg-r {
            animation: none !important;
          }
        }
      `}</style>
      <svg viewBox="0 0 100 100" width={size} height={size}>
        {/* Wheel — rim + spokes + hub, all rotating together */}
        <g
          className="nova-wheel-spin"
          style={{
            transformOrigin: '50px 50px',
            animation: `nova-wheel-spin 2.6s linear infinite`,
            animationPlayState: playState,
          }}
        >
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            opacity="0.85"
          />
          {spokes}
          <circle cx="50" cy="50" r="4" fill="currentColor" />
        </g>

        {/* Nova at the bottom of the wheel — bobs vertically */}
        <g
          className="nova-wheel-bob"
          style={{
            transformOrigin: '50px 78px',
            animation: `nova-wheel-bob 0.65s ease-in-out infinite`,
            animationPlayState: playState,
          }}
        >
          {/* Antenna with Saturn ring + dot */}
          <line
            x1="50"
            y1="62"
            x2="50"
            y2="55"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <ellipse
            cx="50"
            cy="54"
            rx="3.2"
            ry="1"
            fill="none"
            stroke="currentColor"
            strokeWidth="0.9"
          />
          <circle cx="50" cy="54" r="1.2" fill="currentColor" />

          {/* TV-set head — rounded rect with a darker inner screen */}
          <rect
            x="40"
            y="63"
            width="20"
            height="14"
            rx="3"
            ry="3"
            fill="hsl(var(--background))"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <rect
            x="42.5"
            y="65.5"
            width="15"
            height="9"
            rx="1.5"
            ry="1.5"
            fill="currentColor"
            opacity="0.18"
          />

          {/* Eyes — two upward blue triangles (Nova's "happy" pose) */}
          <polygon
            points="46,72 48.5,67.5 51,72"
            fill="hsl(var(--lunar-teal, 195 75% 60%))"
          />
          <polygon
            points="53,72 55.5,67.5 58,72"
            fill="hsl(var(--lunar-teal, 195 75% 60%))"
          />

          {/* Two legs — scissor in opposite phase */}
          <g
            className="nova-wheel-leg-l"
            style={{
              transformOrigin: '46px 77px',
              animation: `nova-wheel-leg-l 0.32s ease-in-out infinite`,
              animationPlayState: playState,
            }}
          >
            <line
              x1="46"
              y1="77"
              x2="44"
              y2="83"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
          <g
            className="nova-wheel-leg-r"
            style={{
              transformOrigin: '54px 77px',
              animation: `nova-wheel-leg-r 0.32s ease-in-out infinite`,
              animationPlayState: playState,
            }}
          >
            <line
              x1="54"
              y1="77"
              x2="56"
              y2="83"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </g>
        </g>
      </svg>
    </span>
  );
}

export default NovaHamsterWheel;